import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import JobCard from './components/JobCard';
import FilterPanel from './components/FilterPanel';
import SavedJobsPanel from './components/SavedJobsPanel';
import QuickStatsBar from './components/QuickStatsBar';
import EmptyState from './components/EmptyState';
import RetryIndicator from '../../components/ui/RetryIndicator';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import jobService from '../../services/jobService';
import { supabase } from '../../lib/supabase';
import { useRetryState } from '../../hooks/useRetryState';
import { retryWithBackoff } from '../../utils/retryWithBackoff';

const ContractorJobBrowser = () => {
  const { userProfile } = useAuth();
  const [filters, setFilters] = useState({
    workType: 'all',
    locationRadius: '50',
    sortBy: 'newest',
    rateRange: { min: '', max: '' },
    requiredSkills: [],
    availability: { startDate: '', endDate: '' }
  });

  const [savedJobIds, setSavedJobIds] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [jobSeekers, setJobSeekers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('jobs'); // 'jobs', 'employers', 'jobseekers'
  const { retryState, startRetry, updateRetry, completeRetry, resetRetry } = useRetryState();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      startRetry();
      
      // Load jobs for contractors and affiliates
      const jobsData = await retryWithBackoff(
        () => jobService?.getAllJobs(),
        {},
        updateRetry
      );
      setJobs(jobsData || []);

      // Load employers (clients) - contractors and affiliates can see them
      const { data: employersData } = await retryWithBackoff(
        async () => {
          const result = await supabase
            ?.from('user_profiles')
            ?.select('id, full_name, company_name, location, email, phone, avatar_url')
            ?.eq('role', 'client')
            ?.eq('is_active', true);
          if (result?.error) throw result?.error;
          return result;
        },
        {},
        updateRetry
      );
      setEmployers(employersData || []);

      // Load job seekers (contractors) - only affiliates can see them
      if (userProfile?.role === 'affiliate') {
        const { data: jobSeekersData } = await retryWithBackoff(
          async () => {
            const result = await supabase
              ?.from('user_profiles')
              ?.select('id, full_name, company_name, location, email, phone, avatar_url')
              ?.eq('role', 'contractor')
              ?.eq('is_active', true);
            if (result?.error) throw result?.error;
            return result;
          },
          {},
          updateRetry
        );
        setJobSeekers(jobSeekersData || []);
      }
      
      completeRetry(true);
    } catch (err) {
      console.error('Error loading data:', err);
      completeRetry(false, err?.message);
    } finally {
      setLoading(false);
      setTimeout(resetRetry, 3000);
    }
  };

  const mockJobs = [];

  const [filteredJobs, setFilteredJobs] = useState([]);

  const quickStats = {
    availableJobs: mockJobs?.length,
    averageRate: mockJobs?.reduce((sum, job) => sum + job?.hourlyRate, 0) / mockJobs?.length,
    nearbyJobs: mockJobs?.filter(job => job?.distance <= 15)?.length,
    newToday: mockJobs?.filter(job => {
      const jobDate = new Date(job.startDate);
      const today = new Date();
      return jobDate?.toDateString() === today?.toDateString();
    })?.length
  };

  useEffect(() => {
    let filtered = [...jobs];

    if (filters?.workType !== 'all') {
      filtered = filtered?.filter(job => job?.work_type === filters?.workType);
    }

    if (filters?.locationRadius !== 'all') {
      const maxDistance = parseInt(filters?.locationRadius);
      filtered = filtered?.filter(job => job?.distance <= maxDistance);
    }

    if (filters?.rateRange?.min) {
      filtered = filtered?.filter(job => job?.hourlyRate >= parseFloat(filters?.rateRange?.min));
    }

    if (filters?.rateRange?.max) {
      filtered = filtered?.filter(job => job?.hourlyRate <= parseFloat(filters?.rateRange?.max));
    }

    if (filters?.requiredSkills?.length > 0) {
      filtered = filtered?.filter(job =>
        filters?.requiredSkills?.some(skill => job?.requiredSkills?.includes(skill))
      );
    }

    if (filters?.availability?.startDate) {
      filtered = filtered?.filter(job =>
        new Date(job.startDate) >= new Date(filters.availability.startDate)
      );
    }

    if (filters?.availability?.endDate) {
      filtered = filtered?.filter(job =>
        new Date(job.startDate) <= new Date(filters.availability.endDate)
      );
    }

    switch (filters?.sortBy) {
      case 'rate-high':
        filtered?.sort((a, b) => b?.hourlyRate - a?.hourlyRate);
        break;
      case 'rate-low':
        filtered?.sort((a, b) => a?.hourlyRate - b?.hourlyRate);
        break;
      case 'distance':
        filtered?.sort((a, b) => a?.distance - b?.distance);
        break;
      case 'deadline':
        filtered?.sort((a, b) => new Date(a.applicationDeadline) - new Date(b.applicationDeadline));
        break;
      default:
        filtered?.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
    }

    setFilteredJobs(filtered);
  }, [filters]);

  const handleSaveJob = (jobId) => {
    setSavedJobIds(prev =>
      prev?.includes(jobId)
        ? prev?.filter(id => id !== jobId)
        : [...prev, jobId]
    );
  };

  const handleResetFilters = () => {
    setFilters({
      workType: 'all',
      locationRadius: '50',
      sortBy: 'newest',
      rateRange: { min: '', max: '' },
      requiredSkills: [],
      availability: { startDate: '', endDate: '' }
    });
  };

  const savedJobs = mockJobs?.filter(job => savedJobIds?.includes(job?.id));

  return (
    <MainLayout userRole="contractor" userName="Jan de Vries">
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading font-semibold text-2xl md:text-3xl lg:text-4xl text-foreground mb-2">
              Opdrachten Zoeken
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Vind de perfecte technische installatie opdrachten in jouw regio
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            iconName="SlidersHorizontal"
            iconPosition="left"
            className="lg:hidden"
          >
            {showFilters ? 'Verberg filters' : 'Toon filters'}
          </Button>
        </div>

        <QuickStatsBar stats={quickStats} />

        <RetryIndicator 
          isRetrying={retryState?.isRetrying}
          attempt={retryState?.attempt}
          maxRetries={retryState?.maxRetries}
          delay={retryState?.delay}
          error={retryState?.error}
          success={retryState?.success}
          className="mb-4"
        />

        <div className="grid lg:grid-cols-12 gap-6">
          <div className={`lg:col-span-3 ${showFilters ? 'block' : 'hidden lg:block'}`}>
            <div className="space-y-4">
              <FilterPanel
                filters={filters}
                onFilterChange={setFilters}
                onResetFilters={handleResetFilters}
                jobCount={filteredJobs?.length}
              />
              <SavedJobsPanel
                savedJobs={savedJobs}
                onViewSaved={() => console.log('View saved jobs')}
                onClearSaved={() => setSavedJobIds([])}
              />
            </div>
          </div>

          <div className="lg:col-span-9">
            {filteredJobs?.length === 0 ? (
              <EmptyState onResetFilters={handleResetFilters} />
            ) : (
              <div className="grid md:grid-cols-2 gap-4 md:gap-5 lg:gap-6">
                {filteredJobs?.map(job => (
                  <JobCard
                    key={job?.id}
                    job={job}
                    onSaveJob={handleSaveJob}
                    isSaved={savedJobIds?.includes(job?.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default ContractorJobBrowser;